import { useState, useEffect, useMemo } from 'react';
import type { CarteConfiguration, BaseMapConfig as BaseMapConfigType, BoundsConfig, InteractionsConfig, LatLng } from '../../types/map';
import { API_URL as apiUrl, SERVER_BASE, resolveAssetUrl } from '../../config/api';

export type BaseMapConfig = BaseMapConfigType;

export interface TileProps {
  url: string;
  attribution: string;
  minZoom: number;
  maxZoom: number;
  tileSize: number;
  subdomains?: string[];
  noWrap?: boolean;
}

/** Un fond de carte résolu avec ses TileProps prêtes à l'emploi */
export interface ResolvedBaseMap {
  id?: string;
  nom: string;
  tileProps: TileProps;
  maxBounds?: BoundsConfig | null;
}

export interface MapConfig {
  centre: LatLng;
  zoom: number;
  zoomMin: number;
  zoomMax: number;
  couleurTheme: string;
  afficherControles: boolean;
  afficherEchelle: boolean;
  afficherPleinEcran: boolean;
  interactionsActivees: InteractionsConfig;
  maxBounds: BoundsConfig | null;
  dupliquerCarte: boolean;
  /** URL absolue de l'illustration du panneau de recherche, ou undefined. */
  illustrationUrl?: string;
  /** URL absolue de l'illustration mobile du panneau de recherche, ou undefined. */
  illustrationUrlMobile?: string;
}

const DEFAULT_MAP_CONFIG: MapConfig = {
  centre: { lat: 48.8566, lng: 2.3522 },
  zoom: 5,
  zoomMin: 5,
  zoomMax: 18,
  couleurTheme: '#007bff',
  afficherControles: true,
  afficherEchelle: false,
  afficherPleinEcran: true,
  interactionsActivees: { zoom: true, pan: true, selection: true },
  maxBounds: null,
  dupliquerCarte: false,
  illustrationUrl: undefined,
  illustrationUrlMobile: undefined,
};

export interface UseBaseMapReturn {
  /** Liste ordonnée des fonds de carte résolus (le premier = par défaut) */
  resolvedBaseMaps: ResolvedBaseMap[];
  /** @deprecated Compat — premier fond de carte ou null */
  baseMap: BaseMapConfig | null;
  setBaseMap: (config: BaseMapConfig | null) => void;
  /** @deprecated Compat — tileProps du premier fond */
  tileProps: TileProps;
  serverBase: string;
  couleurFond: string | null;
  mapConfig: MapConfig;
  configLoaded: boolean;
  carteNom: string;
}

/** Migration douce : convertir ancien baseMap unique → baseMaps tableau */
function migrateBaseMaps(config: CarteConfiguration): BaseMapConfig[] {
  if (Array.isArray(config.baseMaps) && config.baseMaps.length > 0) return config.baseMaps;
  if (config.baseMap && config.baseMap.type) {
    const { nom: existingNom, ...rest } = config.baseMap;
    return [{ nom: existingNom || 'Carte configurée', ...rest }];
  }
  return [];
}

/** Résoudre un BaseMapConfig en TileProps */
function resolveTileProps(bm: BaseMapConfig, serverBase: string, noWrap: boolean): TileProps {
  if (bm.type === 'local' && bm.localName) {
    const fmt = bm.format || 'png';
    return {
      url: `${serverBase}/tiles/${bm.localName}/{z}/{x}/{y}.${fmt}`,
      attribution: bm.attribution || '',
      minZoom: bm.minZoom ?? 0,
      maxZoom: bm.maxZoom ?? 19,
      tileSize: bm.tileSize ?? 256,
      noWrap,
    };
  }
  if (bm.type === 'remote' && bm.urlTemplate) {
    return {
      url: bm.urlTemplate,
      attribution: bm.attribution || '',
      subdomains: bm.subdomains,
      minZoom: bm.minZoom ?? 0,
      maxZoom: bm.maxZoom ?? 19,
      tileSize: bm.tileSize ?? 256,
      noWrap,
    };
  }
  // Fallback should not happen with well-configured data, but return empty
  return { url: '', attribution: '', minZoom: 0, maxZoom: 19, tileSize: 256, noWrap };
}

export const useBaseMap = (slug?: string): UseBaseMapReturn => {
  const [baseMaps, setBaseMaps] = useState<BaseMapConfig[]>([]);
  const [couleurFond, setCouleurFond] = useState<string | null>(null);
  const [mapConfig, setMapConfig] = useState<MapConfig>(DEFAULT_MAP_CONFIG);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [carteNom, setCarteNom] = useState<string>('');

  const serverBase = SERVER_BASE;

  useEffect(() => {
    if (!slug) return;
    
    const loadConfig = async () => {
      try {
        const resp = await fetch(`${apiUrl}/cartes/${slug}/data`).catch(() => null);
        if (!resp || !resp.ok) return;
        
        const data = await resp.json();
        const config: CarteConfiguration | undefined = data?.metadata?.carte?.configuration;
        const nom: string = data?.metadata?.carte?.nom || '';
        setCarteNom(nom);
        
        if (config) {
          // BaseMaps (tableau, avec migration douce)
          setBaseMaps(migrateBaseMaps(config));
          
          // Couleur de fond
          setCouleurFond(config.couleurFond || null);
          
          // Full map configuration
          const resolvedZoomMin = config.zoomMin ?? DEFAULT_MAP_CONFIG.zoomMin;
          const resolvedZoomMax = Math.max(config.zoomMax ?? DEFAULT_MAP_CONFIG.zoomMax, resolvedZoomMin);
          const resolvedZoom = Math.min(Math.max(config.zoom ?? resolvedZoomMin, resolvedZoomMin), resolvedZoomMax);

          setMapConfig({
            centre: config.centre ?? DEFAULT_MAP_CONFIG.centre,
            zoom: resolvedZoom,
            zoomMin: resolvedZoomMin,
            zoomMax: resolvedZoomMax,
            couleurTheme: config.couleurTheme ?? DEFAULT_MAP_CONFIG.couleurTheme,
            afficherControles: config.afficherControles ?? DEFAULT_MAP_CONFIG.afficherControles,
            afficherEchelle: config.afficherEchelle ?? DEFAULT_MAP_CONFIG.afficherEchelle,
            afficherPleinEcran: config.afficherPleinEcran ?? DEFAULT_MAP_CONFIG.afficherPleinEcran,
            interactionsActivees: {
              zoom: config.interactionsActivees?.zoom ?? true,
              pan: config.interactionsActivees?.pan ?? true,
              selection: config.interactionsActivees?.selection ?? true,
            },
            maxBounds: config.maxBounds ?? null,
            dupliquerCarte: config.dupliquerCarte ?? false,
            illustrationUrl: config.illustrationUrl
              ? resolveAssetUrl(config.illustrationUrl)
              : undefined,
            illustrationUrlMobile: config.illustrationUrlMobile
              ? resolveAssetUrl(config.illustrationUrlMobile)
              : undefined,
          });
          setConfigLoaded(true);
        }
      } catch {
        // Silently ignore loading errors
      }
    };
    
    loadConfig();
  }, [slug, serverBase]);

  // Résoudre tous les fonds de carte en TileProps
  const resolvedBaseMaps = useMemo((): ResolvedBaseMap[] => {
    const noWrap = !mapConfig.dupliquerCarte;
    return baseMaps.map(bm => ({
      id: bm.id,
      nom: bm.nom || 'Sans nom',
      tileProps: resolveTileProps(bm, serverBase, noWrap),
      maxBounds: bm.maxBounds ?? null,
    }));
  }, [baseMaps, serverBase, mapConfig.dupliquerCarte]);

  // Compat: premier fond
  const baseMap = baseMaps.length > 0 ? baseMaps[0] : null;
  const tileProps = resolvedBaseMaps.length > 0
    ? resolvedBaseMaps[0].tileProps
    : { url: '', attribution: '', minZoom: 0, maxZoom: 19, tileSize: 256, noWrap: !mapConfig.dupliquerCarte };
  const setBaseMap = (config: BaseMapConfig | null) => {
    setBaseMaps(config ? [config] : []);
  };

  return { resolvedBaseMaps, baseMap, setBaseMap, tileProps, serverBase, couleurFond, mapConfig, configLoaded, carteNom };
};
